import "src/style/components/ExportWorkStatementComponent.scss";

import {
    Box,
    Button,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    TextField,
    ToggleButton,
    Typography,
    useTheme,
} from "@mui/material";
import {
    DataGrid,
    GridActionsCellItem,
    GridColDef,
    GridEventListener,
    GridRowEditStopReasons,
    GridRowId,
    GridRowModel,
    GridRowModes,
    GridRowModesModel,
    GridRowsProp,
    GridSlotProps,
    GridToolbarContainer,
} from "@mui/x-data-grid";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";

import { ChangeEvent, useEffect, useState } from "react";
import LoadingText from "src/components/LoadingText";
import { useAppSelector } from "src/hooks/useAppSelector";
import {
    ExportWorkStatementRequest,
    GetEurRateDto,
    OfferType,
    Timesheet,
    WorkStatementOtherItems,
} from "src/types";
import { fetchWithAuth, getTarget } from "src/utils/api";

declare module "@mui/x-data-grid" {
    interface ToolbarPropsOverrides {
        setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void;
        setRowModesModel: (
            newModel: (oldModel: GridRowModesModel) => GridRowModesModel,
        ) => void;
    }
}

function EditToolbar(props: GridSlotProps["toolbar"]) {
    const { setRows, setRowModesModel } = props;

    const handleClick = () => {
        const id = crypto.randomUUID();
        setRows((oldRows) => [
            ...oldRows,
            { id, description: "", amount: 0, currency: "EUR", isNew: true },
        ]);
        setRowModesModel((oldModel) => ({
            ...oldModel,
            [id]: { mode: GridRowModes.Edit, fieldToFocus: "description" },
        }));
    };

    return (
        <GridToolbarContainer>
            <Button
                color="secondary"
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={handleClick}
            >
                Add record
            </Button>
        </GridToolbarContainer>
    );
}

export default function ExportWorkStatementComponent(): React.ReactElement {
    const errors = useAppSelector((state) => state.app.errors);
    const employee = useAppSelector((state) => state.user.employee);
    const theme = useTheme();

    const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
    const [selectedTimesheet, setSelectedTimesheet] = useState<Timesheet>();
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<JSX.Element>();
    const [rph, setRph] = useState<number>(0);
    const [e2RConv, setE2RConv] = useState<number>(5);
    const [includeRonColumn, setIncludeRonColumn] = useState<boolean>(true);

    const [rows, setRows] = useState<GridRowsProp>([]);
    const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

    const handleRowEditStop: GridEventListener<"rowEditStop"> = (
        params,
        event,
    ) => {
        if (params.reason === GridRowEditStopReasons.rowFocusOut) {
            event.defaultMuiPrevented = true;
        }
    };

    const handleEditClick = (id: GridRowId) => () => {
        setRowModesModel({
            ...rowModesModel,
            [id]: { mode: GridRowModes.Edit },
        });
    };

    const handleSaveClick = (id: GridRowId) => () => {
        setRowModesModel({
            ...rowModesModel,
            [id]: { mode: GridRowModes.View },
        });
    };

    const handleDeleteClick = (id: GridRowId) => () => {
        setRows(rows.filter((row) => row.id !== id));
    };

    const handleCancelClick = (id: GridRowId) => () => {
        setRowModesModel({
            ...rowModesModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        });

        const editedRow = rows.find((row) => row.id === id);
        if (editedRow!.isNew) {
            setRows(rows.filter((row) => row.id !== id));
        }
    };

    const processRowUpdate = (newRow: GridRowModel) => {
        const updatedRow = { ...newRow, isNew: false };
        setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
        return updatedRow;
    };

    const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
        setRowModesModel(newRowModesModel);
    };

    const columns: GridColDef[] = [
        {
            field: "description",
            headerName: "Description",
            editable: true,
            flex: 1,
            minWidth: 150,
        },
        {
            field: "amount",
            headerName: "Amount",
            type: "number",
            align: "left",
            headerAlign: "left",
            editable: true,
            minWidth: 100,
        },
        {
            field: "currency",
            headerName: "Currency",
            editable: true,
            type: "singleSelect",
            valueOptions: includeRonColumn ? ["EUR", "RON"] : ["EUR"],
            minWidth: 100,
        },
        {
            field: "actions",
            type: "actions",
            headerName: "Actions",
            cellClassName: "actions",
            minWidth: 100,
            getActions: ({ id }) => {
                const isInEditMode =
                    rowModesModel[id]?.mode === GridRowModes.Edit;

                if (isInEditMode) {
                    return [
                        <GridActionsCellItem
                            icon={<SaveIcon color="success" />}
                            label="Save"
                            sx={{
                                color: "primary.main",
                            }}
                            onClick={handleSaveClick(id)}
                        />,
                        <GridActionsCellItem
                            icon={<CancelIcon color="error" />}
                            label="Cancel"
                            className="textPrimary"
                            onClick={handleCancelClick(id)}
                            color="inherit"
                        />,
                    ];
                }

                return [
                    <GridActionsCellItem
                        icon={<EditIcon color="secondary" />}
                        label="Edit"
                        className="textPrimary"
                        onClick={handleEditClick(id)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        icon={<DeleteIcon color="error" />}
                        label="Delete"
                        onClick={handleDeleteClick(id)}
                        color="inherit"
                    />,
                ];
            },
        },
    ];

    const handleExtractData = (): WorkStatementOtherItems[] => {
        const dataArray: WorkStatementOtherItems[] = Array.from(rows).map(
            (row) => ({
                description: row.description,
                amount: row.amount,
                currency: row.currency,
            }),
        );
        return dataArray;
    };

    const getEurRate = async () => {
        await fetchWithAuth<GetEurRateDto>(
            getTarget() + "/api/ExportTimesheets/eur-rate",
        ).then(async (_result) => {
            setE2RConv(_result.rate);
        });
    };

    const getTimesheets = async () => {
        await fetchWithAuth<Timesheet[]>(
            getTarget() +
                "/api/ZohoTimesheet?approvalStatuses=Approved,Pending",
        ).then(async (_timesheets) => {
            setTimesheets(_timesheets);
            if (_timesheets.length > 0) {
                setSelectedTimesheet(_timesheets[0]);
            }
        });
    };

    const handleSelectedTimesheetChange = (event: SelectChangeEvent): void => {
        const _timesheetId = event.target.value as string;
        const _timesheet = timesheets.find(
            (_t) => _t.recordId === _timesheetId,
        ) as Timesheet;

        if (_timesheet) {
            setSelectedTimesheet(_timesheet);
        }
    };

    const handleRphChange = (event: ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;

        const regex = /^\d*\.?\d{0,4}$/;
        if (regex.test(inputValue)) {
            setRph(parseFloat(inputValue));
        }
    };

    const handleE2RConvChange = (event: ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;

        const regex = /^\d*\.?\d{0,4}$/;
        if (regex.test(inputValue)) {
            setE2RConv(parseFloat(inputValue));
        }
    };

    const handleExport = async (): Promise<void> => {
        setLoading(true);

        const requestBody: ExportWorkStatementRequest = {
            timesheet: selectedTimesheet!,
            employee: employee!,
            ratePerHour: rph!,
            eurToRonConversionRate: includeRonColumn ? e2RConv : undefined,
            otherItems: handleExtractData(),
            includeRonColumn,
        };

        const request = new Request(
            getTarget() + "/api/exportTimesheets/work-statement",
            {
                method: "post",
                body: JSON.stringify(requestBody),
                headers: {
                    "Content-type": "application/json; charset=UTF-8",
                },
            },
        );

        await fetchWithAuth<Blob>(request)
            .catch((e: Error) => {
                setMessage(
                    <Typography style={{ color: theme.palette.error.main }}>
                        {errors[0]?.message || e.message}
                    </Typography>,
                );
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        setLoading(true);
        Promise.all([getEurRate(), getTimesheets()]).finally(() =>
            setLoading(false),
        );
    }, []);

    return (
        <div className="utils-component__section">
            <Typography
                fontSize={22}
                display="block"
                fontFamily="Syne"
                fontWeight={600}
                gutterBottom
                marginTop={2}
            >
                Generate <span style={{ fontWeight: 800 }}>Work Statement</span>
            </Typography>
            <Typography>
                Easily generate your Work Statement document. A PDF file will be
                exported, containing all the work items from the selected
                timesheet. <br />
                The total ammount is calculated based on the{" "}
                <strong>hours in the timesheet</strong> and the information you
                manually input (<strong>rate per hour</strong> and{" "}
                <strong>EUR to RON conversion rate</strong>).
            </Typography>
            <div className="utils-component__section__form export-ws__form">
                <Select
                    value={selectedTimesheet ? selectedTimesheet.recordId : ""}
                    onChange={handleSelectedTimesheetChange}
                    displayEmpty
                    inputProps={{
                        "aria-label": "Without label",
                        MenuProps: { disableScrollLock: true },
                    }}
                    sx={{
                        width: "100%",
                        maxWidth: 300,
                        height: "fit-content",
                    }}
                >
                    {timesheets.map((timesheet) => (
                        <MenuItem
                            value={timesheet.recordId}
                            key={timesheet.recordId}
                        >
                            {timesheet.timesheetName}
                        </MenuItem>
                    ))}
                </Select>
                <div className="export-ws__form__other-items">
                    <Typography variant="h6">Other items</Typography>
                    <Typography>
                        Add other items to the Work Statement like travel cost,
                        service fee adjustments etc.
                    </Typography>
                    <Box
                        className="export-ws__datagrid-wrapper"
                        sx={{
                            width: "100%",
                            "& .actions": {
                                color: "text.secondary",
                            },
                            "& .textPrimary": {
                                color: "text.primary",
                            },
                        }}
                    >
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            editMode="row"
                            rowModesModel={rowModesModel}
                            onRowModesModelChange={handleRowModesModelChange}
                            onRowEditStop={handleRowEditStop}
                            processRowUpdate={processRowUpdate}
                            slots={{ toolbar: EditToolbar }}
                            slotProps={{
                                toolbar: { setRows, setRowModesModel },
                            }}
                        />
                    </Box>
                </div>
                <TextField
                    type="number"
                    onChange={handleRphChange}
                    value={rph}
                    slotProps={{ htmlInput: { step: "1", min: "0" } }}
                    label={
                        employee?.offerType === OfferType.Fixed
                            ? "Fixed rate"
                            : "Rate per hour"
                    }
                />
                <TextField
                    type="number"
                    onChange={handleE2RConvChange}
                    value={e2RConv}
                    slotProps={{
                        htmlInput: { step: "0.0001", min: "0" },
                    }}
                    label="EUR to RON conversion rate"
                    disabled={!includeRonColumn}
                />
                <div className="export-ws__form__inputs__checkbox">
                    <div className="export-ws__form__inputs__checkbox__button">
                        <ToggleButton
                            value="check"
                            size="small"
                            selected={includeRonColumn}
                            onChange={() => {
                                setIncludeRonColumn(!includeRonColumn);
                            }}
                        >
                            <CheckIcon
                                sx={{
                                    visibility: includeRonColumn
                                        ? "visible"
                                        : "hidden",
                                }}
                            />
                        </ToggleButton>
                        <Typography component="span">
                            Include RON column
                        </Typography>
                    </div>

                    <Typography variant="caption" sx={{ marginTop: 18 }}>
                        <em>
                            * People outside Romania should disable the RON
                            column.
                        </em>
                    </Typography>
                </div>
            </div>

            <Stack
                direction="row"
                spacing={3}
                sx={{ alignItems: "center", height: "48px" }}
                className="export-ws__form__actions"
            >
                <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    onClick={handleExport}
                    disabled={loading || !e2RConv || !rph}
                >
                    generate Work Statement
                </Button>
                {loading && <LoadingText />}
            </Stack>
            <div className="utils-component__section__message">{message}</div>
            <Typography variant="caption">
                <em>
                    <strong>Important!</strong> Please make sure the sum on the
                    Work Statement document <strong>exactly matches</strong>{" "}
                    with your Invoice.
                </em>
            </Typography>
        </div>
    );
}
